// Lightweight client-side search engine for journal entries
// Handles full-text search, fuzzy matching, and relevance scoring

/**
 * Normalize text for searching
 * - Lowercase
 * - Remove special characters
 * - Trim whitespace
 */
function normalizeText(text) {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ') // Replace special chars with spaces
    .replace(/\s+/g, ' ') // Collapse multiple spaces
    .trim();
}

/**
 * Tokenize text into searchable terms
 * Removes common stop words
 */
const STOP_WORDS = new Set([
  'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from',
  'has', 'he', 'in', 'is', 'it', 'its', 'of', 'on', 'that', 'the',
  'to', 'was', 'will', 'with', 'i', 'you', 'my', 'me'
]);

function tokenize(text) {
  const normalized = normalizeText(text);
  const tokens = normalized.split(' ').filter(token => {
    return token.length > 1 && !STOP_WORDS.has(token);
  });
  return tokens;
}

/**
 * Calculate Levenshtein distance for fuzzy matching
 * Returns distance between two strings (lower = more similar)
 */
function levenshteinDistance(str1, str2) {
  const len1 = str1.length;
  const len2 = str2.length;
  const matrix = Array(len1 + 1).fill(null).map(() => Array(len2 + 1).fill(null));

  for (let i = 0; i <= len1; i++) matrix[i][0] = i;
  for (let j = 0; j <= len2; j++) matrix[0][j] = j;

  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,      // deletion
        matrix[i][j - 1] + 1,      // insertion
        matrix[i - 1][j - 1] + cost // substitution
      );
    }
  }

  return matrix[len1][len2];
}

/**
 * Check if query matches text with fuzzy tolerance
 * Returns match score (0-100, higher = better match)
 */
function fuzzyMatch(query, text, tolerance = 2) {
  const normalizedQuery = normalizeText(query);
  const normalizedText = normalizeText(text);

  // Exact match
  if (normalizedText.includes(normalizedQuery)) {
    if (normalizedText === normalizedQuery) return 100; // Perfect match
    if (normalizedText.startsWith(normalizedQuery)) return 90; // Starts with
    return 80; // Contains exact
  }

  // Fuzzy match - check each word
  const queryTokens = tokenize(query);
  const textTokens = tokenize(text);

  let matchScore = 0;

  for (const queryToken of queryTokens) {
    let bestTokenScore = 0;

    for (const textToken of textTokens) {
      // Exact token match
      if (textToken === queryToken) {
        bestTokenScore = Math.max(bestTokenScore, 50);
      }
      // Starts with
      else if (textToken.startsWith(queryToken) || queryToken.startsWith(textToken)) {
        bestTokenScore = Math.max(bestTokenScore, 40);
      }
      // Fuzzy match
      else {
        const distance = levenshteinDistance(queryToken, textToken);
        const maxLen = Math.max(queryToken.length, textToken.length);
        if (distance <= tolerance && distance < maxLen) {
          const similarity = ((maxLen - distance) / maxLen) * 30;
          bestTokenScore = Math.max(bestTokenScore, similarity);
        }
      }
    }

    matchScore += bestTokenScore;
  }

  return matchScore / queryTokens.length;
}

/**
 * Extract snippet of text around match for preview
 */
function extractSnippet(text, query, contextChars = 80) {
  if (!text || !query) return '';

  const normalizedText = normalizeText(text);
  const normalizedQuery = normalizeText(query);

  const index = normalizedText.indexOf(normalizedQuery);

  if (index === -1) {
    // No exact match, return first N chars
    return text.substring(0, contextChars * 2) + (text.length > contextChars * 2 ? '...' : '');
  }

  // Extract context around match
  const start = Math.max(0, index - contextChars);
  const end = Math.min(text.length, index + normalizedQuery.length + contextChars);

  let snippet = text.substring(start, end);

  if (start > 0) snippet = '...' + snippet;
  if (end < text.length) snippet = snippet + '...';

  return snippet;
}

/**
 * Highlight matching terms in text
 * Returns array of {text, highlight} objects
 */
export function highlightMatches(text, query) {
  if (!text || !query) return [{ text, highlight: false }];

  const normalizedQuery = normalizeText(query);
  const queryTokens = tokenize(query);

  // Find all match positions
  const matches = [];
  const normalizedText = normalizeText(text);

  // Exact phrase match
  let index = normalizedText.indexOf(normalizedQuery);
  if (index !== -1) {
    matches.push({ start: index, end: index + normalizedQuery.length });
  } else {
    // Token matches
    for (const token of queryTokens) {
      let pos = 0;
      while ((pos = normalizedText.indexOf(token, pos)) !== -1) {
        matches.push({ start: pos, end: pos + token.length });
        pos += token.length;
      }
    }
  }

  if (matches.length === 0) {
    return [{ text, highlight: false }];
  }

  // Sort and merge overlapping matches
  matches.sort((a, b) => a.start - b.start);
  const merged = [];
  let current = matches[0];

  for (let i = 1; i < matches.length; i++) {
    if (matches[i].start <= current.end) {
      current.end = Math.max(current.end, matches[i].end);
    } else {
      merged.push(current);
      current = matches[i];
    }
  }
  merged.push(current);

  // Build result array
  const result = [];
  let lastEnd = 0;

  // Map normalized positions back to original text
  let normalizedPos = 0;
  let originalPos = 0;
  const positionMap = [];

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const normalizedChar = normalizeText(char);

    if (normalizedChar) {
      positionMap[normalizedPos] = i;
      normalizedPos++;
    }
  }

  for (const match of merged) {
    const realStart = positionMap[match.start] || match.start;
    const realEnd = positionMap[match.end - 1] + 1 || match.end;

    if (realStart > lastEnd) {
      result.push({ text: text.substring(lastEnd, realStart), highlight: false });
    }

    result.push({ text: text.substring(realStart, realEnd), highlight: true });
    lastEnd = realEnd;
  }

  if (lastEnd < text.length) {
    result.push({ text: text.substring(lastEnd), highlight: false });
  }

  return result;
}

/**
 * Calculate age decay score
 * Recent entries get higher scores
 */
function calculateAgeScore(createdAt, maxAgeDays = 365) {
  if (!createdAt) return 0.5;

  const now = Date.now();
  let timestamp;

  // Handle Firestore Timestamp
  if (createdAt.toDate && typeof createdAt.toDate === 'function') {
    timestamp = createdAt.toDate().getTime();
  } else if (createdAt instanceof Date) {
    timestamp = createdAt.getTime();
  } else if (typeof createdAt === 'number') {
    timestamp = createdAt;
  } else {
    return 0.5; // Default score if can't parse date
  }

  const ageMs = now - timestamp;
  const ageDays = ageMs / (1000 * 60 * 60 * 24);

  if (ageDays < 0) return 1; // Future date?
  if (ageDays > maxAgeDays) return 0.1; // Very old

  // Exponential decay
  return Math.max(0.1, 1 - (ageDays / maxAgeDays) * 0.9);
}

/**
 * Main search function
 * Returns sorted array of matching entries with scores and snippets
 */
export function searchEntries(entries, query, options = {}) {
  const {
    filters = {},
    limit = 50,
    includeSnippets = true,
    minScore = 10
  } = options;

  if (!query || query.trim().length === 0) {
    return [];
  }

  const normalizedQuery = normalizeText(query);
  const results = [];

  for (const entry of entries) {
    // Apply filters first (faster than scoring)
    if (filters.dateRange) {
      const entryDate = entry.createdAt?.toDate?.() || new Date(entry.createdAt);
      if (entryDate < filters.dateRange.start || entryDate > filters.dateRange.end) {
        continue;
      }
    }

    if (filters.moods && filters.moods.length > 0) {
      if (!entry.mood || !filters.moods.includes(entry.mood)) {
        continue;
      }
    }

    if (filters.modes && filters.modes.length > 0) {
      if (!entry.mode || !filters.modes.includes(entry.mode)) {
        continue;
      }
    }

    // Calculate match scores
    let totalScore = 0;
    let bestSnippet = '';
    let matchedFields = [];

    // Search in content (highest weight)
    if (entry.content) {
      const contentScore = fuzzyMatch(normalizedQuery, entry.content);
      if (contentScore > 0) {
        totalScore += contentScore * 3; // 3x weight for content
        matchedFields.push('content');
        if (includeSnippets && !bestSnippet) {
          bestSnippet = extractSnippet(entry.content, query);
        }
      }
    }

    // Search in extracted text (OCR)
    if (entry.extractedText) {
      const extractedScore = fuzzyMatch(normalizedQuery, entry.extractedText);
      if (extractedScore > 0) {
        totalScore += extractedScore * 2.5; // 2.5x weight
        matchedFields.push('extracted');
        if (includeSnippets && !bestSnippet) {
          bestSnippet = extractSnippet(entry.extractedText, query);
        }
      }
    }

    // Search in mood
    if (entry.mood) {
      const moodScore = fuzzyMatch(normalizedQuery, entry.mood);
      if (moodScore > 0) {
        totalScore += moodScore * 1.5; // 1.5x weight
        matchedFields.push('mood');
      }
    }

    // Skip if no matches
    if (totalScore === 0 || totalScore < minScore) {
      continue;
    }

    // Apply age decay
    const ageScore = calculateAgeScore(entry.createdAt);
    totalScore *= (0.7 + ageScore * 0.3); // 70% relevance + 30% recency

    results.push({
      entry,
      score: totalScore,
      snippet: bestSnippet,
      matchedFields,
      highlights: includeSnippets ? highlightMatches(entry.content || entry.extractedText || '', query) : null
    });
  }

  // Sort by score (highest first)
  results.sort((a, b) => b.score - a.score);

  // Limit results
  return results.slice(0, limit);
}

/**
 * Get search suggestions based on query
 * Returns array of suggested search terms
 */
export function getSearchSuggestions(entries, query) {
  if (!query || query.length < 2) return [];

  const suggestions = new Set();
  const normalizedQuery = normalizeText(query);

  // Extract common phrases from entries
  for (const entry of entries) {
    const content = entry.content || entry.extractedText || '';
    const tokens = tokenize(content);

    for (const token of tokens) {
      if (token.startsWith(normalizedQuery) && token.length > query.length) {
        suggestions.add(token);
        if (suggestions.size >= 5) break;
      }
    }

    if (suggestions.size >= 5) break;
  }

  return Array.from(suggestions).slice(0, 5);
}

export default {
  searchEntries,
  highlightMatches,
  getSearchSuggestions,
  normalizeText,
  tokenize
};
